import { atLeast, isSuperAdmin } from '../auth/useMyPermissions';
import type { PermissionLevel } from '../auth/useMyPermissions';

/**
 * Who may decide a supplier sample submission as a whole — approve it for
 * production, hold it, or send it back.
 *
 * One sentence, stated once, and the same sentence the database says in
 * may_decide_supplier_submission() (20270107000100): the tap_admin ACCOUNT
 * role, or Admin on Supplier Reviews in the permissions matrix. Kyu set two
 * people to Admin on Supplier Reviews on 3 September and said "make them be
 * able to approve"; until then only the account role counted, and the screen
 * he was using to hand out access had no effect on this one decision.
 *
 * Why 'admin' and not 'member': a member already judges the individual
 * options (submission_options_update lets any TAP user), and the production
 * decision is the one that commits the factory and the client's money.
 *
 * Why an undecided level reads as NO here, where canSee reads it as yes: the
 * database refuses a caller without a row (check_user_permission treats a
 * missing row as no access), and drawing a button the database will refuse
 * was the exact defect on the CG order page — "Approve for production" shown
 * to members, failing with submission_decision_requires_admin.
 *
 * The contract test (decide.test.ts) holds this and the SQL to the same two
 * conditions.
 */

export const DECIDER_MODULE = 'supplier_reviews';
export const DECIDER_LEVEL: PermissionLevel = 'admin';

export interface DecidingProfile {
  account_type: string;
  internal_role: string | null;
}

export function canDecideSubmission(
  profile: DecidingProfile | null,
  levels: Record<string, PermissionLevel> | undefined,
): boolean {
  if (profile === null || profile.account_type !== 'tap') {
    return false;
  }
  if (profile.internal_role === 'tap_admin') {
    return true;
  }
  if (levels === undefined) {
    return false;
  }
  if (isSuperAdmin(levels)) {
    return true;
  }
  const level = levels[DECIDER_MODULE];
  return level !== undefined && atLeast(level, DECIDER_LEVEL);
}
